/**
 * wizardSteps — construtores da lista de passos do wizard (uma pergunta por
 * passo) para as duas famílias de solicitação:
 *   - passosAssistencia (ASM/AST/ASP): cabeçalho + N peças + revisão
 *   - passosRequisicao  (RQA/RQI):     cabeçalho + N itens + fotos + revisão
 *
 * Cada peça/item vira um bloco de passos "campo" seguidos, e ao final da
 * ÚLTIMA peça/item entra um passo "boundary" ("adicionar outra? / continuar").
 * A regra de obrigatoriedade vive em `valido()` (trava o Próxima) e no flag
 * `obrigatorio` (asterisco vermelho) — os dois espelham a validação do envio.
 *
 * Os construtores recebem um `ctx` com estado + handlers do container; as
 * funções `render` fecham sobre esse estado (o container reconstrói a lista a
 * cada render, então as closures ficam sempre atuais).
 */
import React from "react";
import { FiCheck, FiPlus } from "react-icons/fi";

import {
  MOTIVOS_PECA, MOTIVOS_REQUISICAO, SETORES_ASP, UNIDADES_MEDIDA_RQI,
} from "../../../services/solicitacoes";
import ComboboxBusca from "./ComboboxBusca";
import ProdutoBusca from "./ProdutoBusca";
import FitaBordaSimulador from "./FitaBordaSimulador";
import VisualizadorPeca from "./VisualizadorPeca";
import FotosInput from "./FotosInput";
import {
  contarBordasComFita, corDaFita, descreverFitas, erroDetalheDoMotivo,
  formatarCorMdf, formatarMedidas, MOTIVO_DETALHE_MIN, MOTIVO_OUTRO,
  parseDecimal, validarMedidaMdf,
} from "./utils";
import {
  Asterisco, BadgeVeio, BoundaryBotoes, BoundaryCard, BoundaryIcone,
  BoundaryTitulo, BtnFantasma, BtnPrimario, EspessuraDisplay, Field, Hint,
  InlineError, Input, Label, Linha2, RevisaoAcoes, RevisaoEditar, RevisaoGrupo,
  RevisaoGrupoTopo, RevisaoLinha, RevisaoLista, RevisaoPecaAcao, RevisaoTitulo,
  SegmentoBotoes, SegmentoBtn, Select, StepperBox, StepperBtn, StepperValor,
  ToggleBtn, ToggleLinha,
} from "./styles";

const MAX_FOTOS_PECA = 3;
const MAX_FOTOS_REQUISICAO = 5;

/**
 * Pendura a saída ("Remover peça N") em TODOS os passos de um bloco de
 * peça/item, e faz o "Voltar" do PRIMEIRO passo descartar o bloco quando ele
 * ainda está em branco.
 *
 * Existe porque "Adicionar outra peça" insere o bloco novo ANTES do boundary e
 * da revisão: o primeiro passo nasce inválido (trava o "Próxima") e o único
 * "Remover" morava na revisão, depois do bloco — inalcançável. Quem clicava
 * sem querer ficava obrigado a preencher uma peça que não queria.
 *
 * Bloco em branco some sem perguntar (não há o que perder); com algo digitado,
 * confirma antes. Depois de remover, salta para o boundary via
 * `jumpQuandoPronto` — a lista de passos encolhe e o índice do boundary muda,
 * então não dá para navegar por índice já calculado.
 */
function decorarSaidaDoBloco(passos, inicio, opcoes) {
  const { rotulo, confirmacao, podeRemover, intocado, remover } = opcoes;
  if (!podeRemover) return; // nunca deixa a solicitação ficar sem nenhum bloco

  const descartar = (nav) => {
    if (!intocado && !window.confirm(confirmacao)) return;
    remover();
    nav.jumpQuandoPronto("boundary");
  };

  for (let k = inicio; k < passos.length; k += 1) {
    passos[k].acaoDescartar = { rotulo, executar: descartar };
  }
  // Só o primeiro passo: voltar dali com o bloco em branco = desistir dele.
  // Com algo digitado, "Voltar" segue sendo navegação pura — ninguém perde
  // trabalho por apertar voltar.
  if (intocado && passos[inicio]) {
    passos[inicio].aoVoltar = (nav) => {
      remover();
      nav.jumpQuandoPronto("boundary");
      return true;
    };
  }
}

function ToggleSimNao({ rotulo, valor, aoMudar }) {
  return (
    <ToggleLinha>
      <span>{rotulo}</span>
      <div>
        <ToggleBtn type="button" $ativo={valor} onClick={() => aoMudar(true)}>Sim</ToggleBtn>
        <ToggleBtn type="button" $ativo={!valor} onClick={() => aoMudar(false)}>Não</ToggleBtn>
      </div>
    </ToggleLinha>
  );
}

function LinhaRev({ rotulo, valor, chave, nav }) {
  const texto = valor != null && String(valor).trim() ? valor : "—";
  return (
    <RevisaoLinha>
      <span className="rot">{rotulo}</span>
      <span className="val">{texto}</span>
      {chave && nav && (
        <RevisaoEditar type="button" onClick={() => nav.editar(chave)}>Editar</RevisaoEditar>
      )}
    </RevisaoLinha>
  );
}

const nomeMotivo = (v) => MOTIVOS_PECA.find((m) => m.value === v)?.label || "—";
const nomeMotivoReq = (v) => MOTIVOS_REQUISICAO.find((m) => m.value === v)?.label || v || "—";
const nomeSetor = (v) => SETORES_ASP.find((s) => s.value === v)?.label || v || "—";

// ==========================================================================
// Cabeçalho (compartilhado — o que muda por tipo é sinalizado no ctx)
// ==========================================================================

function passosCabecalho(ctx) {
  const { tipo, cab, clientes, clientesCarregando, erros, mudarCab } = ctx;
  const passos = [];

  passos.push({
    key: "cab.num_pedido", grupo: "Dados gerais", titulo: "Número do pedido",
    opcional: true, kind: "campo",
    render: () => (
      <Field>
        <Input
          autoFocus
          inputMode="numeric"
          value={cab.num_pedido}
          placeholder="Ex.: 1234"
          onChange={(e) => mudarCab("num_pedido", e.target.value)}
        />
        <Hint>Número do pedido no sistema, se houver.</Hint>
      </Field>
    ),
  });

  passos.push({
    key: "cab.cliente", grupo: "Dados gerais", titulo: "Cliente", obrigatorio: true,
    kind: "campo",
    // Obrigatório: o backend exige cliente (_resolver_cliente) — tem de ser
    // escolhido da lista (digitar sem selecionar não vale).
    valido: () => !!cab.cliente,
    render: () => (
      <Field>
        <ComboboxBusca
          opcoes={clientes}
          carregando={clientesCarregando}
          valor={cab.cliente}
          placeholder="Buscar cliente cadastrado…"
          onSelecionar={(o) => mudarCab("cliente", o)}
          onTextoChange={(t) => mudarCab("clienteTexto", t)}
        />
        {erros.cliente ? (
          <InlineError>{erros.cliente}</InlineError>
        ) : (
          <Hint>Somente clientes já cadastrados no sistema.</Hint>
        )}
      </Field>
    ),
  });

  passos.push({
    key: "cab.supervisor", grupo: "Dados gerais", titulo: "Supervisor",
    opcional: true, kind: "campo",
    render: () => (
      <Field>
        <Input
          value={cab.supervisor_nome}
          placeholder="Nome do supervisor"
          onChange={(e) => mudarCab("supervisor_nome", e.target.value)}
        />
      </Field>
    ),
  });

  if (tipo === "ASM" || tipo === "AST") {
    passos.push({
      key: "cab.lider", grupo: "Dados gerais", titulo: "Líder de montagem",
      obrigatorio: true, kind: "campo",
      valido: () => !!cab.lider_montagem_nome.trim(),
      render: () => (
        <Field>
          <Input
            autoFocus
            value={cab.lider_montagem_nome}
            placeholder="Nome do líder"
            onChange={(e) => mudarCab("lider_montagem_nome", e.target.value)}
          />
          {erros.lider_montagem_nome && (
            <InlineError>{erros.lider_montagem_nome}</InlineError>
          )}
        </Field>
      ),
    });
  }

  if (tipo === "ASP") {
    passos.push({
      key: "cab.setor", grupo: "Dados gerais", titulo: "Setor", obrigatorio: true,
      kind: "campo", valido: () => !!cab.setor,
      render: () => (
        <Field>
          <Select value={cab.setor} onChange={(e) => mudarCab("setor", e.target.value)}>
            <option value="">Selecione o setor…</option>
            {SETORES_ASP.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </Select>
          {erros.setor && <InlineError>{erros.setor}</InlineError>}
        </Field>
      ),
    });
    passos.push({
      key: "cab.lote", grupo: "Dados gerais", titulo: "Lote de produção",
      opcional: true, kind: "campo",
      render: () => (
        <Field>
          <Input
            value={cab.lote_producao}
            placeholder="Identificação do lote"
            onChange={(e) => mudarCab("lote_producao", e.target.value)}
          />
        </Field>
      ),
    });
  }

  return passos;
}

function linhasRevisaoCab(ctx, nav) {
  const { tipo, cab } = ctx;
  return (
    <RevisaoGrupo>
      <RevisaoGrupoTopo>
        <RevisaoTitulo>Dados gerais</RevisaoTitulo>
      </RevisaoGrupoTopo>
      <LinhaRev rotulo="Pedido" valor={cab.num_pedido} chave="cab.num_pedido" nav={nav} />
      <LinhaRev rotulo="Cliente" valor={cab.cliente?.nome} chave="cab.cliente" nav={nav} />
      <LinhaRev rotulo="Supervisor" valor={cab.supervisor_nome} chave="cab.supervisor" nav={nav} />
      {(tipo === "ASM" || tipo === "AST") && (
        <LinhaRev rotulo="Líder" valor={cab.lider_montagem_nome} chave="cab.lider" nav={nav} />
      )}
      {tipo === "ASP" && (
        <LinhaRev rotulo="Setor" valor={nomeSetor(cab.setor)} chave="cab.setor" nav={nav} />
      )}
      {tipo === "ASP" && (
        <LinhaRev rotulo="Lote" valor={cab.lote_producao} chave="cab.lote" nav={nav} />
      )}
    </RevisaoGrupo>
  );
}

// ==========================================================================
// Assistência (ASM/AST/ASP) — cabeçalho + peças + revisão
// ==========================================================================

export function passosAssistencia(ctx) {
  const {
    pecas, materiais, materiaisCarregando, fitas, fitasCarregando, erros,
    mudarPeca, mudarPecaVarios, adicionarPeca, removerPeca, pecaIntocada,
  } = ctx;

  const passos = [...passosCabecalho(ctx)];

  pecas.forEach((p, i) => {
    const grupo = `Peça ${i + 1}`;
    // Índice do 1º passo desta peça — usado no fim do bloco para pendurar a
    // saída ("Remover esta peça") em todos os passos dela de uma vez.
    const inicioDoBloco = passos.length;
    const nova = p.natureza === "NOVA";
    const bordas = contarBordasComFita(p);
    const mp = (campo, valor) => mudarPeca(i, campo, valor);
    const mpv = (vals) => mudarPecaVarios(i, vals);
    const erroDe = (c) => erros[`p${i}_${c}`];

    passos.push({
      key: `peca.${p.uid}.ambiente`, grupo, titulo: "Ambiente da peça",
      obrigatorio: true, kind: "campo", valido: () => !!p.ambiente.trim(),
      render: () => (
        <Field>
          <Input
            autoFocus
            value={p.ambiente}
            placeholder="Ex.: Cozinha"
            onChange={(e) => mp("ambiente", e.target.value)}
          />
          {erroDe("ambiente") && <InlineError>{erroDe("ambiente")}</InlineError>}
        </Field>
      ),
    });

    passos.push({
      key: `peca.${p.uid}.descricao`, grupo, titulo: "Descrição da peça",
      obrigatorio: true, kind: "campo", valido: () => !!p.descricao.trim(),
      render: () => (
        <Field>
          <Input
            autoFocus
            value={p.descricao}
            placeholder="Ex.: Porta esquerda do balcão"
            onChange={(e) => mp("descricao", e.target.value)}
          />
          {erroDe("descricao") && <InlineError>{erroDe("descricao")}</InlineError>}
        </Field>
      ),
    });

    passos.push({
      key: `peca.${p.uid}.natureza`, grupo, titulo: "É peça nova ou só mão de obra?",
      kind: "campo",
      render: () => (
        <SegmentoBotoes>
          <SegmentoBtn type="button" $ativo={nova} onClick={() => mp("natureza", "NOVA")}>
            Peça nova
          </SegmentoBtn>
          <SegmentoBtn type="button" $ativo={!nova} onClick={() => mp("natureza", "MAO_DE_OBRA")}>
            Só mão de obra
          </SegmentoBtn>
        </SegmentoBotoes>
      ),
    });

    passos.push({
      key: `peca.${p.uid}.modulo`, grupo, titulo: "Módulo",
      obrigatorio: nova, opcional: !nova, kind: "campo",
      valido: () => (nova ? !!p.modulo.trim() : true),
      render: () => (
        <Linha2>
          <Field>
            <Label>Módulo{nova ? "" : " (opcional)"}</Label>
            <Input
              autoFocus
              value={p.modulo}
              placeholder="Ex.: Balcão pia"
              onChange={(e) => mp("modulo", e.target.value)}
            />
            {erroDe("modulo") && <InlineError>{erroDe("modulo")}</InlineError>}
          </Field>
          <Field>
            <Label>Ref. (opcional)</Label>
            <Input
              value={p.ref_modulo}
              placeholder="Ex.: M03"
              onChange={(e) => mp("ref_modulo", e.target.value)}
            />
          </Field>
        </Linha2>
      ),
    });

    if (nova) {
      passos.push({
        key: `peca.${p.uid}.material`, grupo, titulo: "Material (Cores MDF)",
        obrigatorio: true, kind: "campo", valido: () => !!p.material_id,
        render: () => {
          const selecionado = p.material_id
            ? materiais.find((m) => String(m.id) === String(p.material_id)) || {
                id: p.material_id,
                label: formatarCorMdf(p.material_nome, p.espessura_mm) || p.material_nome,
              }
            : null;
          const selecionar = (o) => {
            if (!o) {
              mpv({ material_id: null, material_nome: "", material_codigo: "", espessura_mm: "" });
              return;
            }
            mpv({
              material_id: o.id,
              material_nome: o.descricao || "",
              material_codigo: o.codigo || "",
              espessura_mm: o.espessura ? String(o.espessura) : "",
            });
          };
          return (
            <Field>
              <ComboboxBusca
                opcoes={materiais}
                carregando={materiaisCarregando}
                valor={selecionado}
                placeholder="Buscar por código ou descrição…"
                onSelecionar={selecionar}
              />
              {erroDe("material") ? (
                <InlineError>{erroDe("material")}</InlineError>
              ) : (
                <Hint>Selecionar preenche código e espessura automaticamente.</Hint>
              )}
            </Field>
          );
        },
      });
    }

    passos.push({
      key: `peca.${p.uid}.medidas`, grupo, titulo: "Medidas da peça",
      obrigatorio: nova, opcional: !nova,
      descricao: "O comprimento é sempre medido no sentido do veio da chapa.",
      kind: "campo",
      // Peça NOVA: medidas obrigatórias (o corte precisa delas — backend
      // rejeita sem) e dentro dos limites 20–2730 × 20–1830. Mão de obra:
      // medida é só referência — sem trava (espelha o backend).
      valido: () => !nova || (
        parseDecimal(p.comprimento_mm) != null
        && parseDecimal(p.largura_mm) != null
        && !validarMedidaMdf("comprimento_mm", p.comprimento_mm)
        && !validarMedidaMdf("largura_mm", p.largura_mm)
      ),
      render: () => {
        const erroComprimento = (nova && validarMedidaMdf("comprimento_mm", p.comprimento_mm))
          || erroDe("comprimento_mm");
        const erroLargura = (nova && validarMedidaMdf("largura_mm", p.largura_mm))
          || erroDe("largura_mm");
        return (
        <>
          <Field>
            <Label>Comprimento (mm) <BadgeVeio>⇕ SENTIDO DO VEIO</BadgeVeio></Label>
            <Input
              autoFocus
              inputMode="decimal"
              value={p.comprimento_mm}
              placeholder="Ex.: 778"
              onChange={(e) => mp("comprimento_mm", e.target.value)}
            />
            {erroComprimento && <InlineError>{erroComprimento}</InlineError>}
          </Field>
          <Linha2>
            <Field>
              <Label>Largura (mm)</Label>
              <Input
                inputMode="decimal"
                value={p.largura_mm}
                placeholder="Ex.: 649"
                onChange={(e) => mp("largura_mm", e.target.value)}
              />
            </Field>
            <Field>
              <Label>Espessura</Label>
              <EspessuraDisplay>{p.espessura_mm ? `${p.espessura_mm} mm` : "—"}</EspessuraDisplay>
            </Field>
          </Linha2>
          {erroLargura && <InlineError>{erroLargura}</InlineError>}
          <Hint>A espessura vem do material selecionado.</Hint>
          <Field>
            <Label>Observação (opcional)</Label>
            <Input
              maxLength={26}
              value={p.observacao || ""}
              placeholder="Ex.: rebaixo p/ puxador"
              onChange={(e) => mp("observacao", e.target.value)}
            />
            <Hint style={{ textAlign: "right" }}>{(p.observacao || "").length}/26</Hint>
          </Field>
        </>
        );
      },
    });

    passos.push({
      key: `peca.${p.uid}.quantidade`, grupo, titulo: "Quantidade", obrigatorio: true,
      kind: "campo", valido: () => (parseInt(p.quantidade, 10) || 0) >= 1,
      render: () => {
        const mudarQ = (delta) => {
          const atual = parseInt(p.quantidade, 10) || 1;
          mp("quantidade", String(Math.max(1, atual + delta)));
        };
        return (
          <Field>
            <StepperBox>
              <StepperBtn
                type="button"
                aria-label="Diminuir quantidade"
                disabled={(parseInt(p.quantidade, 10) || 1) <= 1}
                onClick={() => mudarQ(-1)}
              >
                −
              </StepperBtn>
              <StepperValor
                inputMode="numeric"
                value={p.quantidade}
                onChange={(e) => mp("quantidade", e.target.value.replace(/\D/g, ""))}
              />
              <StepperBtn type="button" aria-label="Aumentar quantidade" onClick={() => mudarQ(1)}>
                +
              </StepperBtn>
            </StepperBox>
            {erroDe("quantidade") && <InlineError>{erroDe("quantidade")}</InlineError>}
          </Field>
        );
      },
    });

    passos.push({
      key: `peca.${p.uid}.fita`, grupo, titulo: "Onde vai fita de borda?",
      opcional: bordas === 0, obrigatorio: bordas > 0, kind: "campo",
      descricao: "Toque nas bordas da peça que recebem fita.",
      valido: () => (bordas > 0 ? !!p.fita_nome.trim() : true),
      render: () => {
        const corAtual = corDaFita(p.fita_nome);
        const fitaSelecionada = corAtual
          ? (fitas || []).find((f) => f.cor === corAtual) || {
              id: corAtual, label: corAtual,
            }
          : null;
        return (
          <>
            <FitaBordaSimulador
              comprimento={p.comprimento_mm}
              largura={p.largura_mm}
              bordas={p}
              onToggleBorda={(campo) => mp(campo, !p[campo])}
              onDefinirTodas={(v) =>
                mpv({ fita_frente: v, fita_traseira: v, fita_le: v, fita_ld: v })
              }
            />
            {bordas > 0 && (
              <Field style={{ marginTop: 12 }}>
                <Label>Cor da fita <Asterisco> *</Asterisco></Label>
                <ComboboxBusca
                  opcoes={fitas || []}
                  carregando={fitasCarregando}
                  valor={fitaSelecionada}
                  placeholder="Buscar pela cor…"
                  onSelecionar={(o) => mp("fita_nome", o ? o.cor : "")}
                />
                {erroDe("fita_nome") ? (
                  <InlineError>{erroDe("fita_nome")}</InlineError>
                ) : (
                  <Hint>A largura certa é escolhida pela espessura do MDF.</Hint>
                )}
              </Field>
            )}
          </>
        );
      },
    });

    passos.push({
      key: `peca.${p.uid}.caracteristicas`, grupo, titulo: "Características da peça",
      obrigatorio: p.usinagem || p.pintura, opcional: !(p.usinagem || p.pintura),
      kind: "campo",
      valido: () =>
        (!p.usinagem || !!p.usinagem_descricao.trim()) &&
        (!p.pintura || !!p.pintura_descricao.trim()),
      render: () => (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <ToggleSimNao rotulo="Usinagem" valor={p.usinagem} aoMudar={(v) => mp("usinagem", v)} />
          {p.usinagem && (
            <Field>
              <Label>Descreva a usinagem <Asterisco> *</Asterisco></Label>
              <Input
                autoFocus
                value={p.usinagem_descricao}
                placeholder="Ex.: rebaixo p/ dobradiça, furo Ø35…"
                onChange={(e) => mp("usinagem_descricao", e.target.value)}
              />
              {erroDe("usinagem_descricao") && (
                <InlineError>{erroDe("usinagem_descricao")}</InlineError>
              )}
            </Field>
          )}
          <ToggleSimNao rotulo="Pintura" valor={p.pintura} aoMudar={(v) => mp("pintura", v)} />
          {p.pintura && (
            <Field>
              <Label>Descreva a pintura <Asterisco> *</Asterisco></Label>
              <Input
                value={p.pintura_descricao}
                placeholder="Ex.: laca branca fosca, 2 faces…"
                onChange={(e) => mp("pintura_descricao", e.target.value)}
              />
              {erroDe("pintura_descricao") && (
                <InlineError>{erroDe("pintura_descricao")}</InlineError>
              )}
            </Field>
          )}
        </div>
      ),
    });

    passos.push({
      key: `peca.${p.uid}.motivo`, grupo, titulo: "Motivo da solicitação",
      obrigatorio: true, kind: "campo",
      // "Outro" sozinho não diz nada a quem lê depois: exige o motivo escrito.
      valido: () =>
        !!p.motivo_categoria
        && !erroDetalheDoMotivo(p.motivo_categoria, p.motivo_detalhe),
      render: () => {
        const exigeDetalhe = p.motivo_categoria === MOTIVO_OUTRO;
        return (
          <>
            <Field>
              <Select
                value={p.motivo_categoria}
                onChange={(e) => mp("motivo_categoria", e.target.value)}
              >
                <option value="">Selecione o motivo…</option>
                {MOTIVOS_PECA.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </Select>
              {erroDe("motivo") && <InlineError>{erroDe("motivo")}</InlineError>}
            </Field>
            <Field style={{ marginTop: 12 }}>
              <Label>
                {exigeDetalhe ? "Qual o motivo?" : "Detalhe (opcional)"}
                {exigeDetalhe && <Asterisco>*</Asterisco>}
              </Label>
              <Input
                value={p.motivo_detalhe}
                placeholder={
                  exigeDetalhe ? "Escreva o motivo" : "Complemento do motivo"
                }
                onChange={(e) => mp("motivo_detalhe", e.target.value)}
              />
              {exigeDetalhe && (
                <Hint>Mínimo de {MOTIVO_DETALHE_MIN} caracteres.</Hint>
              )}
              {erroDe("motivo_detalhe") && (
                <InlineError>{erroDe("motivo_detalhe")}</InlineError>
              )}
            </Field>
          </>
        );
      },
    });

    passos.push({
      key: `peca.${p.uid}.fotos`, grupo, titulo: "Fotos da peça", opcional: true,
      kind: "campo",
      render: () => (
        <FotosInput
          fotos={p.fotos}
          max={MAX_FOTOS_PECA}
          onChange={(fotos) => mp("fotos", fotos)}
        />
      ),
    });

    decorarSaidaDoBloco(passos, inicioDoBloco, {
      rotulo: `Remover peça ${i + 1}`,
      confirmacao: `Remover a peça ${i + 1}? O que já foi preenchido nela será perdido.`,
      podeRemover: pecas.length > 1,
      intocado: pecaIntocada?.(p),
      remover: () => removerPeca(i),
    });
  });

  // Fechamento da última peça: adicionar outra ou seguir para a revisão.
  passos.push({
    key: "boundary", grupo: `Peça ${pecas.length}`, kind: "boundary",
    render: (nav) => (
      <BoundaryCard>
        <BoundaryIcone><FiCheck size={26} /></BoundaryIcone>
        <BoundaryTitulo>Peça {pecas.length} pronta</BoundaryTitulo>
        <BoundaryBotoes>
          <BtnFantasma
            type="button"
            onClick={() => {
              const uid = adicionarPeca();
              nav.jumpQuandoPronto(`peca.${uid}.ambiente`);
            }}
          >
            <FiPlus size={18} /> Adicionar outra peça
          </BtnFantasma>
          <BtnPrimario type="button" onClick={() => nav.proximo()}>
            Concluir e revisar
          </BtnPrimario>
        </BoundaryBotoes>
      </BoundaryCard>
    ),
  });

  passos.push(passoRevisaoAssistencia(ctx));
  return passos;
}

function passoRevisaoAssistencia(ctx) {
  const {
    pecas, materiais, adicionarPeca, removerPeca, duplicarPeca,
  } = ctx;
  return {
    key: "revisao", grupo: "Revisão", kind: "revisao",
    titulo: "Confira antes de enviar",
    render: (nav) => (
      <RevisaoLista>
        {linhasRevisaoCab(ctx, nav)}

        {pecas.map((p, i) => (
          <RevisaoGrupo key={p.uid}>
            <RevisaoGrupoTopo>
              <RevisaoTitulo>Peça {i + 1}</RevisaoTitulo>
              <RevisaoAcoes>
                <RevisaoPecaAcao
                  type="button"
                  onClick={() => {
                    const uid = duplicarPeca(i);
                    if (uid) nav.jumpQuandoPronto(`peca.${uid}.ambiente`);
                  }}
                >
                  Duplicar
                </RevisaoPecaAcao>
                {pecas.length > 1 && (
                  <RevisaoPecaAcao type="button" $danger onClick={() => removerPeca(i)}>
                    Remover
                  </RevisaoPecaAcao>
                )}
              </RevisaoAcoes>
            </RevisaoGrupoTopo>
            {/* Desenho ANTES dos campos: na revisao a pessoa confere a peca,
                e ver a forma responde mais rapido que reler as medidas. */}
            <VisualizadorPeca peca={p} materiais={materiais} />
            <LinhaRev rotulo="Ambiente" valor={p.ambiente} chave={`peca.${p.uid}.ambiente`} nav={nav} />
            <LinhaRev rotulo="Descrição" valor={p.descricao} chave={`peca.${p.uid}.descricao`} nav={nav} />
            <LinhaRev
              rotulo="Tipo"
              valor={p.natureza === "NOVA" ? "Peça nova" : "Só mão de obra"}
              chave={`peca.${p.uid}.natureza`}
              nav={nav}
            />
            {p.natureza === "NOVA" && (
              <LinhaRev
                rotulo="Material"
                valor={formatarCorMdf(p.material_nome, p.espessura_mm)}
                chave={`peca.${p.uid}.material`}
                nav={nav}
              />
            )}
            <LinhaRev rotulo="Medidas" valor={formatarMedidas(p)} chave={`peca.${p.uid}.medidas`} nav={nav} />
            <LinhaRev rotulo="Qtd." valor={`${p.quantidade}x`} chave={`peca.${p.uid}.quantidade`} nav={nav} />
            <LinhaRev
              rotulo="Fita"
              valor={contarBordasComFita(p) > 0 ? descreverFitas(p) : "Sem fita"}
              chave={`peca.${p.uid}.fita`}
              nav={nav}
            />
            {p.usinagem && (
              <LinhaRev
                rotulo="Usinagem"
                valor={p.usinagem_descricao}
                chave={`peca.${p.uid}.caracteristicas`}
                nav={nav}
              />
            )}
            {p.pintura && (
              <LinhaRev
                rotulo="Pintura"
                valor={p.pintura_descricao}
                chave={`peca.${p.uid}.caracteristicas`}
                nav={nav}
              />
            )}
            <LinhaRev rotulo="Motivo" valor={nomeMotivo(p.motivo_categoria)} chave={`peca.${p.uid}.motivo`} nav={nav} />
          </RevisaoGrupo>
        ))}

        <BtnFantasma
          type="button"
          onClick={() => {
            const uid = adicionarPeca();
            nav.jumpQuandoPronto(`peca.${uid}.ambiente`);
          }}
        >
          <FiPlus size={18} /> Adicionar peça
        </BtnFantasma>
      </RevisaoLista>
    ),
  };
}

// ==========================================================================
// Requisição (RQA/RQI) — cabeçalho + itens + fotos + revisão
// ==========================================================================

export function passosRequisicao(ctx) {
  const {
    tipo, itens, fotos, erros, mudarItem, adicionarItem, mudarFotos,
    removerItem, itemIntocado,
  } = ctx;
  const ehRQA = tipo === "RQA";
  const ehRQI = tipo === "RQI";

  const passos = [...passosCabecalho(ctx)];

  itens.forEach((it, i) => {
    const grupo = `Item ${i + 1}`;
    const mi = (campo, valor) => mudarItem(i, campo, valor);
    const erroDe = (c) => erros[`i${i}_${c}`];
    const inicioDoBloco = passos.length;

    // Acréscimo de 22/09/2026: achar o item no cadastro. Opcional — quem não
    // acha continua descrevendo à mão nos passos seguintes.
    passos.push({
      key: `item.${it.uid}.produto`, grupo, titulo: "Item do cadastro", opcional: true,
      kind: "campo",
      render: () => (
        <Field>
          <ProdutoBusca
            autoFocus
            valor={it.produto || null}
            onSelecionar={(p) => {
              mi("produto", p);
              if (!p) return;
              mi("descricao", p.nome);
              mi("codigo", p.codigo || "");
              if (ehRQI && UNIDADES_MEDIDA_RQI.includes(p.unidade)) mi("unidade", p.unidade);
            }}
          />
          <Hint>
            Achando o item, o almoxarifado reserva e dá baixa no estoque sozinho.
            Não achou? Siga em frente e descreva à mão.
          </Hint>
        </Field>
      ),
    });

    passos.push({
      key: `item.${it.uid}.codigo`, grupo, titulo: "Código do item", opcional: true,
      kind: "campo",
      render: () => (
        <Field>
          <Input
            autoFocus
            value={it.codigo}
            placeholder="Ex.: FER-0123"
            onChange={(e) => mi("codigo", e.target.value)}
          />
          <Hint>Código do almoxarifado, se souber.</Hint>
        </Field>
      ),
    });

    passos.push({
      key: `item.${it.uid}.descricao`, grupo, titulo: "Descrição do item",
      obrigatorio: true, kind: "campo", valido: () => !!it.descricao.trim(),
      render: () => (
        <Field>
          <Input
            autoFocus
            value={it.descricao}
            placeholder="Ex.: Parafuso 4x40mm"
            onChange={(e) => mi("descricao", e.target.value)}
          />
          {erroDe("descricao") && <InlineError>{erroDe("descricao")}</InlineError>}
        </Field>
      ),
    });

    if (ehRQA) {
      passos.push({
        key: `item.${it.uid}.ambiente`, grupo, titulo: "Ambiente",
        obrigatorio: true, kind: "campo", valido: () => !!it.ambiente.trim(),
        render: () => (
          <Field>
            <Input
              autoFocus
              value={it.ambiente}
              placeholder="Ex.: Cozinha"
              onChange={(e) => mi("ambiente", e.target.value)}
            />
            {erroDe("ambiente") && <InlineError>{erroDe("ambiente")}</InlineError>}
          </Field>
        ),
      });
      passos.push({
        key: `item.${it.uid}.medida`, grupo, titulo: "Medida",
        obrigatorio: true, kind: "campo", valido: () => !!it.medida.trim(),
        render: () => (
          <Field>
            <Input
              autoFocus
              value={it.medida}
              placeholder="Ex.: 4x40mm, Ø35, 1,5 m…"
              onChange={(e) => mi("medida", e.target.value)}
            />
            {erroDe("medida") ? (
              <InlineError>{erroDe("medida")}</InlineError>
            ) : (
              <Hint>Tamanho/medida do item pedido.</Hint>
            )}
          </Field>
        ),
      });
    }

    passos.push({
      key: `item.${it.uid}.quantidade`, grupo, titulo: "Quantidade",
      obrigatorio: true, kind: "campo",
      valido: () => (parseDecimal(it.quantidade) || 0) > 0,
      render: () => (
        <Linha2>
          <Field>
            <Label>Quantidade</Label>
            <Input
              autoFocus
              inputMode="decimal"
              value={it.quantidade}
              placeholder="Ex.: 12"
              onChange={(e) => mi("quantidade", e.target.value)}
            />
            {erroDe("quantidade") && <InlineError>{erroDe("quantidade")}</InlineError>}
          </Field>
          {ehRQI && (
            <Field>
              <Label>Unidade</Label>
              <Select value={it.unidade} onChange={(e) => mi("unidade", e.target.value)}>
                {UNIDADES_MEDIDA_RQI.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </Select>
            </Field>
          )}
        </Linha2>
      ),
    });

    if (ehRQA) {
      passos.push({
        key: `item.${it.uid}.motivo`, grupo, titulo: "Motivo da requisição",
        obrigatorio: true, kind: "campo",
        // "Outro" sozinho não diz nada ao almoxarifado: exige o motivo escrito.
        valido: () =>
          !!it.motivo && !erroDetalheDoMotivo(it.motivo, it.motivo_detalhe),
        render: () => {
          const exigeDetalhe = it.motivo === MOTIVO_OUTRO;
          return (
            <>
              <Field>
                <Select value={it.motivo} onChange={(e) => mi("motivo", e.target.value)}>
                  <option value="">Selecione o motivo…</option>
                  {MOTIVOS_REQUISICAO.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </Select>
                {erroDe("motivo") && <InlineError>{erroDe("motivo")}</InlineError>}
              </Field>
              {it.motivo && (
                <Field style={{ marginTop: 12 }}>
                  <Label>
                    {exigeDetalhe ? "Qual o motivo?" : "Detalhe (opcional)"}
                    {exigeDetalhe && <Asterisco>*</Asterisco>}
                  </Label>
                  <Input
                    value={it.motivo_detalhe}
                    placeholder={
                      exigeDetalhe ? "Escreva o motivo" : "Complemento do motivo"
                    }
                    onChange={(e) => mi("motivo_detalhe", e.target.value)}
                  />
                  {exigeDetalhe && (
                    <Hint>Mínimo de {MOTIVO_DETALHE_MIN} caracteres.</Hint>
                  )}
                  {erroDe("motivo_detalhe") && (
                    <InlineError>{erroDe("motivo_detalhe")}</InlineError>
                  )}
                </Field>
              )}
            </>
          );
        },
      });
    }

    decorarSaidaDoBloco(passos, inicioDoBloco, {
      rotulo: `Remover item ${i + 1}`,
      confirmacao: `Remover o item ${i + 1}? O que já foi preenchido nele será perdido.`,
      podeRemover: itens.length > 1,
      intocado: itemIntocado?.(it),
      remover: () => removerItem(i),
    });
  });

  passos.push({
    key: "boundary", grupo: `Item ${itens.length}`, kind: "boundary",
    render: (nav) => (
      <BoundaryCard>
        <BoundaryIcone><FiCheck size={26} /></BoundaryIcone>
        <BoundaryTitulo>Item {itens.length} pronto</BoundaryTitulo>
        <BoundaryBotoes>
          <BtnFantasma
            type="button"
            onClick={() => {
              const uid = adicionarItem();
              nav.jumpQuandoPronto(`item.${uid}.codigo`);
            }}
          >
            <FiPlus size={18} /> Adicionar outro item
          </BtnFantasma>
          <BtnPrimario type="button" onClick={() => nav.proximo()}>
            Concluir
          </BtnPrimario>
        </BoundaryBotoes>
      </BoundaryCard>
    ),
  });

  passos.push({
    key: "fotos", grupo: "Fotos", titulo: "Fotos da requisição", opcional: true,
    kind: "campo",
    render: () => (
      <FotosInput fotos={fotos} max={MAX_FOTOS_REQUISICAO} onChange={mudarFotos} />
    ),
  });

  passos.push(passoRevisaoRequisicao(ctx));
  return passos;
}

function passoRevisaoRequisicao(ctx) {
  const { tipo, itens, fotos, adicionarItem, removerItem } = ctx;
  const ehRQA = tipo === "RQA";
  const ehRQI = tipo === "RQI";
  return {
    key: "revisao", grupo: "Revisão", kind: "revisao",
    titulo: "Confira antes de enviar",
    render: (nav) => (
      <RevisaoLista>
        {linhasRevisaoCab(ctx, nav)}

        {itens.map((it, i) => (
          <RevisaoGrupo key={it.uid}>
            <RevisaoGrupoTopo>
              <RevisaoTitulo>Item {i + 1}</RevisaoTitulo>
              {itens.length > 1 && (
                <RevisaoAcoes>
                  <RevisaoPecaAcao type="button" $danger onClick={() => removerItem(i)}>
                    Remover
                  </RevisaoPecaAcao>
                </RevisaoAcoes>
              )}
            </RevisaoGrupoTopo>
            {it.codigo && (
              <LinhaRev rotulo="Código" valor={it.codigo} chave={`item.${it.uid}.codigo`} nav={nav} />
            )}
            <LinhaRev rotulo="Descrição" valor={it.descricao} chave={`item.${it.uid}.descricao`} nav={nav} />
            {ehRQA && (
              <LinhaRev rotulo="Ambiente" valor={it.ambiente} chave={`item.${it.uid}.ambiente`} nav={nav} />
            )}
            {ehRQA && (
              <LinhaRev rotulo="Medida" valor={it.medida} chave={`item.${it.uid}.medida`} nav={nav} />
            )}
            <LinhaRev
              rotulo="Qtd."
              valor={`${it.quantidade || "—"}${ehRQI ? ` ${it.unidade}` : ""}`}
              chave={`item.${it.uid}.quantidade`}
              nav={nav}
            />
            {ehRQA && (
              <LinhaRev
                rotulo="Motivo"
                valor={
                  it.motivo
                    ? [nomeMotivoReq(it.motivo), it.motivo_detalhe]
                        .filter(Boolean).join(" — ")
                    : "—"
                }
                chave={`item.${it.uid}.motivo`}
                nav={nav}
              />
            )}
          </RevisaoGrupo>
        ))}

        <RevisaoGrupo>
          <RevisaoGrupoTopo>
            <RevisaoTitulo>Fotos</RevisaoTitulo>
            <RevisaoEditar type="button" onClick={() => nav.editar("fotos")}>Editar</RevisaoEditar>
          </RevisaoGrupoTopo>
          <LinhaRev rotulo="Anexadas" valor={`${fotos.length} foto(s)`} />
        </RevisaoGrupo>

        <BtnFantasma
          type="button"
          onClick={() => {
            const uid = adicionarItem();
            nav.jumpQuandoPronto(`item.${uid}.codigo`);
          }}
        >
          <FiPlus size={18} /> Adicionar item
        </BtnFantasma>
      </RevisaoLista>
    ),
  };
}
